/**
 * Connecting a workspace's GitHub repositories to Teammate Code (spec 083 D1).
 *
 * One screen and a picker, chosen by what the workspace actually has:
 *
 * - still reading the answer — a spinner, and nothing shaped like the surface
 *   it might turn into;
 * - the read itself failed — say so, offer a retry, and still offer to connect,
 *   because "we could not check" is not "you are not connected";
 * - the deployment has no GitHub App registered — an operator problem, so no
 *   button that would 404 on GitHub;
 * - the App exists but this workspace has not installed it — the connect call
 *   to action;
 * - installed — a picker listing exactly the repositories GitHub will let the
 *   agent clone.
 *
 * The picker replaces a free-text `owner/repository` box, whose failure mode was
 * a clone that died tens of seconds later with no explanation of what the user
 * had got wrong.
 *
 * @module components/coding/github-connect
 */

import { useQuery } from "@tanstack/react-query"
import {
  ArrowLeft,
  Check,
  ChevronsUpDown,
  GitBranch,
  Github,
  Github as GithubIcon,
  Loader2,
  Lock,
} from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import {
  getGithubConnectionStatus,
  getGithubInstallUrl,
  listGithubRepos,
  type GithubRepo,
} from "@/services/code-service"

export const githubStatusKey = ["code", "github", "status"] as const
export const githubReposKey = ["code", "github", "repos"] as const

/** Reads the workspace's connection state; the caller branches on it. */
export function useGithubConnection() {
  return useQuery({
    queryKey: githubStatusKey,
    queryFn: getGithubConnectionStatus,
    staleTime: 30_000,
  })
}

/**
 * What the coding surface can offer, decided before anything else renders.
 *
 * "loading" is a state in its own right rather than an optimistic guess at
 * "ready": showing a composer and a repository picker while the answer is still
 * unknown gave a workspace with no installation a working-looking form, a picker
 * that spun, and only then the connect screen.
 */
export type GithubGateState = "loading" | "unavailable" | "unconfigured" | "disconnected" | "ready"

/** Reduces the connection query to the one thing callers branch on. */
export function githubGateState(connection: ReturnType<typeof useGithubConnection>): GithubGateState {
  if (connection.isPending) return "loading"
  // An unreachable status endpoint is not the same as "you have not connected
  // GitHub" — offering the connect button there sends the user off to fix
  // something that is not theirs to fix.
  if (connection.isError) return "unavailable"
  if (connection.data?.configured === false) return "unconfigured"
  return connection.data?.installations[0] === undefined ? "disconnected" : "ready"
}

/**
 * The whole screen when coding cannot reach a repository yet.
 *
 * Rendered in place of the coding shell — no session rail, because a rail of
 * sessions a user cannot start is furniture. The way back out is therefore part
 * of this screen rather than something the sidebar would have provided.
 *
 * Sends the browser to GitHub, where the customer chooses the account and the
 * repositories. That decision belongs on GitHub's screen and not ours, because
 * GitHub is also where it is reviewed and revoked later.
 */
export function GithubGateScreen({
  state,
  appId,
  onRetry,
  retrying = false,
}: {
  state: Exclude<GithubGateState, "ready">
  appId?: string
  /** Re-reads the connection, for the state where reading it is what failed. */
  onRetry?: () => void
  /** True while a retry is in flight, so the button reports it. */
  retrying?: boolean
}) {
  const [starting, setStarting] = useState(false)

  const start = async () => {
    setStarting(true)
    try {
      window.location.href = await getGithubInstallUrl(appId)
    } catch {
      // Leaving the button enabled is the whole recovery: the next click retries.
      setStarting(false)
    }
  }

  const copy = GATE_COPY[state]

  /*
    Offered while the check is failing as well as when it says "not connected".

    A failed check means we do not know whether this workspace has an
    installation — and the one thing a user on this screen is trying to do is
    connect. Withholding the button until we can confirm they need it leaves
    them on a dead end whose only instruction is to reload.
  */
  const canConnect = state === "disconnected" || state === "unavailable"

  return (
    <div
      data-testid={`coding-github-${state}`}
      className="flex h-full w-full flex-col items-center justify-center gap-6 overflow-auto p-6 text-center"
    >
      <Github className="size-24 text-muted-foreground/25" strokeWidth={1.25} aria-hidden />

      <div className="space-y-2">
        <h1 className="text-lg font-semibold">{copy.title}</h1>
        {/*
          The spinner leads the line it belongs to. On its own row it read as a
          separate element with nothing to attach itself to, and overlaid on the
          mark it read as part of the logo rather than as progress.
        */}
        <p className="mx-auto flex max-w-md items-center justify-center gap-2 text-sm text-muted-foreground">
          {state === "loading" && (
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
          )}
          {copy.body}
        </p>
      </div>

      {(canConnect || onRetry !== undefined) && state !== "loading" && (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2">
            {state === "unavailable" && onRetry !== undefined && (
              <Button size="lg" variant="outline" onClick={onRetry} disabled={retrying}>
                {retrying ? "Checking…" : "Try again"}
              </Button>
            )}
            {canConnect && (
              <Button size="lg" onClick={() => void start()} disabled={starting}>
                {starting ? "Opening GitHub…" : "Connect GitHub"}
              </Button>
            )}
          </div>
          {canConnect && (
            <p className="mx-auto max-w-md text-xs text-muted-foreground">
              Installing on an organisation you do not administer sends a request to its
              owners instead.
            </p>
          )}
        </div>
      )}

      {appId !== undefined && (
        <Link
          to={`/app/${appId}/chat`}
          data-testid="coding-github-back"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to main app
        </Link>
      )}
    </div>
  )
}

/** One screen, four reasons to be on it. */
const GATE_COPY: Record<Exclude<GithubGateState, "ready">, { title: string; body: string }> = {
  loading: {
    title: "Checking your GitHub connection",
    body: "One moment…",
  },
  unavailable: {
    title: "Could not check your GitHub connection",
    body:
      "The server did not answer, so we cannot tell whether this workspace is already connected. Try again, or connect GitHub now.",
  },
  unconfigured: {
    title: "Coding is not set up on this deployment",
    body:
      "No GitHub App is configured, so repositories cannot be reached. Ask an administrator to finish the setup.",
  },
  disconnected: {
    title: "Connect GitHub to start coding",
    body:
      "You choose which repositories the agent can see, on GitHub. You can change or revoke that at any time.",
  },
}

export interface GithubRepoPickerProps {
  /** Currently selected repository as `owner/name`, or empty. */
  value: string
  /** Receives the selection, plus the repo's own default branch. */
  onSelect: (repo: GithubRepo) => void
  /** GitHub's page for adding repositories to the installation. */
  manageUrl?: string
  disabled?: boolean
  /**
   * Sized to sit in the composer's toolbar rather than to fill a page.
   *
   * Choosing the repository is part of writing the instruction, not a step
   * before it — so the control belongs on the same row as Plan and the mic, at
   * their scale.
   */
  compact?: boolean
  /**
   * The branch to work from, edited inside the popover.
   *
   * Kept here rather than as a second control on the toolbar: a bordered text
   * box next to a ghost button read as two unrelated things, and a branch is
   * only meaningful once a repository is chosen. Omit both to hide the row.
   */
  branchValue?: string
  onBranchChange?: (value: string) => void
  /**
   * Overrides on the trigger, for hosts whose row is denser than a toolbar.
   *
   * `compact` is sized for the composer's toolbar, where it stands beside Plan
   * and the mic at `h-8`. The tray under the composer is a caption — every other
   * item there is `py-0.5` — so a toolbar-height control in it sets the row's
   * height on its own and the strip reads as a second toolbar.
   */
  className?: string
}

/**
 * Picks from the repositories the installation actually covers.
 *
 * The list is read live on open rather than cached across sessions: a customer
 * can change the selection on GitHub at any moment, and offering a repository
 * that has since been removed reintroduces exactly the silent clone failure this
 * replaces.
 */
export function GithubRepoPicker({
  value,
  onSelect,
  manageUrl,
  disabled = false,
  compact = false,
  branchValue,
  onBranchChange,
  className,
}: GithubRepoPickerProps) {
  const [open, setOpen] = useState(false)

  const repos = useQuery({
    queryKey: githubReposKey,
    queryFn: listGithubRepos,
    enabled: open,
    staleTime: 30_000,
  })

  /** Only the repository name in the toolbar; the owner is rarely the ambiguity. */
  const label = value === "" ? "Repository" : compact ? (value.split("/")[1] ?? value) : value
  const showBranch =
    value !== "" && branchValue !== undefined && onBranchChange !== undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={compact ? "ghost" : "outline"}
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid="coding-repo-picker"
          title={value === "" ? "Choose a repository" : value}
          className={cn(
            "font-normal",
            compact
              ? "h-8 max-w-44 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              : "w-full justify-between",
            className,
          )}
        >
          <GithubIcon className={cn("shrink-0 opacity-70", compact ? "size-3.5" : "hidden")} />
          <span className={cn("truncate", value === "" && "text-muted-foreground")}>{label}</span>
          {/* The branch rides on the same chip, and only when it is worth
              saying — repeating a repository's own default is noise. */}
          {compact && showBranch && (
            <span className="flex min-w-0 items-center gap-0.5 text-muted-foreground/70">
              <span aria-hidden>·</span>
              <GitBranch className="size-3 shrink-0" />
              <span className="truncate">{branchValue}</span>
            </span>
          )}
          <ChevronsUpDown
            className={cn("shrink-0 opacity-50", compact ? "size-3" : "ms-2 size-4")}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent className={cn("p-0", compact ? "w-80" : "w-[--radix-popover-trigger-width]")} align={compact ? "end" : "start"}>
        <Command>
          <CommandInput placeholder="Search repositories…" />
          <CommandList>
            {repos.isError ? (
              // Said plainly rather than left to fall through to "no repository
              // matches", which reads as an empty account and sends the user to
              // GitHub to fix something that is not broken there.
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Could not reach GitHub.
                <button
                  type="button"
                  onClick={() => void repos.refetch()}
                  className="ms-1 underline underline-offset-2 hover:text-foreground"
                >
                  Try again
                </button>
              </div>
            ) : repos.isPending ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Loading repositories…
              </div>
            ) : (
              <>
                <CommandEmpty>
                  <span className="text-sm text-muted-foreground">No repository matches.</span>
                </CommandEmpty>
                <CommandGroup>
                  {(repos.data ?? []).map((repo) => (
                    <CommandItem
                      key={repo.fullName}
                      value={repo.fullName}
                      onSelect={() => {
                        onSelect(repo)
                        setOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          "me-2 size-4",
                          value === repo.fullName ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate font-mono text-xs">
                        {repo.fullName}
                      </span>
                      {repo.private && (
                        <Lock className="ms-2 size-3 shrink-0 text-muted-foreground" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>

        {showBranch && (
          // Below the list, not beside the trigger: a branch only means anything
          // once a repository is chosen, and it is pre-filled with that
          // repository's own default, so most people never touch it.
          <div className="flex items-center gap-2 border-t px-3 py-2">
            <GitBranch className="size-3.5 shrink-0 text-muted-foreground" />
            <label htmlFor="coding-branch" className="text-xs text-muted-foreground">
              Branch
            </label>
            <input
              id="coding-branch"
              data-testid="coding-branch-input"
              value={branchValue}
              onChange={(event) => onBranchChange?.(event.target.value)}
              className="min-w-0 flex-1 bg-transparent text-end font-mono text-xs outline-none placeholder:text-muted-foreground/60"
              placeholder="default"
            />
          </div>
        )}

        {manageUrl !== undefined && (
          // The way out of "my repository is not listed", which is otherwise a
          // dead end: the selection lives on GitHub and is changed there.
          <div className="border-t px-2 py-1.5">
            <a
              href={manageUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Add or remove repositories on GitHub
            </a>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** Human-readable outcome of the install round-trip, keyed by `?github=`. */
export const GITHUB_CALLBACK_MESSAGES: Record<string, string> = {
  connected: "GitHub connected.",
  requested: "Install requested — a GitHub organisation owner needs to approve it.",
  cancelled: "GitHub installation was cancelled.",
  invalid_state: "That install link had expired. Try connecting again.",
  unreadable: "GitHub accepted the install but would not describe it. Try again.",
}
